// apps/web/src/features/inventory/hooks/useVenueInventory.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type {
  VenueInventoryResponse,
  AddVenueInventoryRequest,
  UpdateVenueInventoryRequest,
  SetVenueAddonPricingRequest,
} from '@lov/shared';

// ============================================
// VENUE INVENTORY HOOKS
// ============================================

// Get venue inventory (public for browsing — always returns only available items)
export function useVenueInventory(venueId: string, includeAddons = false) {
  return useQuery<VenueInventoryResponse[]>({
    queryKey: ['venues', venueId, 'inventory', { includeAddons }],
    queryFn: async () => {
      return validatedApi.get(`/venues/${venueId}/inventory`, {
        params: {
          includeAddons: includeAddons ? 'true' : undefined,
        },
      });
    },
    enabled: !!venueId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Get venue inventory for vendor management — returns ALL items including unavailable ones.
// Calls the authenticated GET /vendor/inventory/venues/:venueId endpoint; requires a valid
// vendor JWT. Must NOT be used on public-facing pages.
export function useVendorVenueInventory(venueId: string, includeAddons = false) {
  return useQuery<VenueInventoryResponse[]>({
    queryKey: ['vendor', 'inventory', 'venues', venueId, { includeAddons }],
    queryFn: async () => {
      return validatedApi.get(`/vendor/inventory/venues/${venueId}`, {
        params: {
          includeAddons: includeAddons ? 'true' : undefined,
        },
      });
    },
    enabled: !!venueId,
    staleTime: 0, // always fresh in vendor management context
  });
}

// Get single venue inventory item
export function useVenueInventoryItem(venueId: string, itemId: string) {
  return useQuery<VenueInventoryResponse>({
    queryKey: ['venues', venueId, 'inventory', itemId],
    queryFn: async () => {
      return validatedApi.get(`/venues/${venueId}/inventory/${itemId}`);
    },
    enabled: !!venueId && !!itemId,
  });
}

// Add inventory to venue (Vendor)
export function useAddVenueInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, data }: { venueId: string; data: AddVenueInventoryRequest }) => {
      return validatedApi.post(`/venues/${venueId}/inventory`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['venues', variables.venueId, 'inventory'] });
    },
  });
}

// Update venue inventory (Vendor)
export function useUpdateVenueInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      itemId,
      data,
    }: {
      venueId: string;
      itemId: string;
      data: UpdateVenueInventoryRequest;
    }) => {
      return validatedApi.put(`/venues/${venueId}/inventory/${itemId}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['venues', variables.venueId, 'inventory'] });
      queryClient.invalidateQueries({
        queryKey: ['venues', variables.venueId, 'inventory', variables.itemId],
      });
    },
  });
}

// Remove inventory from venue (Vendor)
export function useRemoveVenueInventory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ venueId, itemId }: { venueId: string; itemId: string }) => {
      await validatedApi.delete(`/venues/${venueId}/inventory/${itemId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['venues', variables.venueId, 'inventory'] });
    },
  });
}

// Set addon pricing (Vendor)
export function useSetVenueAddonPricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      itemId,
      data,
    }: {
      venueId: string;
      itemId: string;
      data: SetVenueAddonPricingRequest;
    }) => {
      return validatedApi.put(`/venues/${venueId}/inventory/${itemId}/addon-pricing`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['venues', variables.venueId, 'inventory'] });
      queryClient.invalidateQueries({
        queryKey: ['venues', variables.venueId, 'inventory', variables.itemId],
      });
    },
  });
}

// Remove addon pricing override (Vendor)
export function useRemoveVenueAddonPricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      venueId,
      itemId,
      addonItemId,
    }: {
      venueId: string;
      itemId: string;
      addonItemId: string;
    }) => {
      await validatedApi.delete(
        `/venues/${venueId}/inventory/${itemId}/addon-pricing/${addonItemId}`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['venues', variables.venueId, 'inventory'] });
      queryClient.invalidateQueries({
        queryKey: ['venues', variables.venueId, 'inventory', variables.itemId],
      });
    },
  });
}
